import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Dropdown, DropdownToggle } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../common/types';
import { useSwitchWorkspace } from '../workspaces/useSwitchWorkspace';
import { WorkspaceSwitcherSection } from '../workspaces/WorkspaceSwitcherSection';
import { ArtistSwitcherMenu } from './ArtistSwitcherMenu';
import { ArtistSwitcherPill } from './ArtistSwitcherPill';
import { AddArtistModal } from './AddArtistModal';
import { useArtistSwitcher } from './useArtistSwitcher';
import { artistSwitcherMessages as messages } from './artistSwitcherMessages';
/* eslint-disable css-modules/no-unused-class -- shared with ArtistSwitcherPill.tsx; each file uses its own slice of it */
import styles from './ArtistSwitcher.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ArtistSwitcherProps {
  /**
   * `topBar` is the pill of the mobile web top bar (design b1): the name only, no count.
   * `compact` is the small pill at the far right of the deep top bar (design: navigation i1).
   */
  variant?: 'sidebar' | 'topBar' | 'compact';
  /**
   * design: navigation i1. On mobile web the pill opens the switcher as a bottom sheet, which the
   * mobile nav owns (`ArtistSwitcherSheet`). With this set, the pill is a plain button, not a
   * dropdown toggle.
   */
  sheet?: ArtistSwitcherSheetControl;
}

export interface ArtistSwitcherSheetControl {
  id: string;
  isOpen: boolean;
  onOpen: () => void;
}

const toggleClass = {
  sidebar: styles.toggle,
  topBar: styles.topBarToggle,
  compact: clsx(styles.topBarToggle, styles.compactToggle),
};

const ArtistSwitcherComponent: React.FC<ArtistSwitcherProps> = ({ variant = 'sidebar', sheet }) => {
  const intl = useIntl();
  const switcher = useArtistSwitcher();
  const { accounts, active, artistsLimit } = switcher;
  const isCompact = variant === 'compact';
  // Controlled, so that a workspace switch can close the menu (design: workspaces a3).
  const [isMenuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const workspaceSwitch = useSwitchWorkspace(closeMenu);
  const { clearFailure } = workspaceSwitch;
  const hasWorkspaces = workspaceSwitch.isVisible;
  // design: workspaces a4. The alert does not come back when the menu opens again.
  const toggleMenu = useCallback(() => {
    clearFailure();
    setMenuOpen((open) => !open);
  }, [clearFailure]);

  if (!accounts) {
    return null;
  }

  // With two or more workspaces, the sidebar and top-bar pills stay even with no artist: the menu
  // is the way to the other workspaces (design: workspaces a1).
  if (accounts.length === 0 && (isCompact || !hasWorkspaces)) {
    if (!isCompact) {
      return null;
    }
    // design: navigation i1 (no artist). The dashed "Add artist" pill of g4.
    return (
      <>
        <button type="button" className={clsx(toggleClass.compact, styles.addPill)} onClick={switcher.openAdd}>
          <FontAwesomeIcon icon={faPlus} />
          <span className={styles.name}>{intl.formatMessage(messages.add)}</span>
        </button>
        <AddArtistModal isOpen={switcher.isAddOpen} onClose={switcher.closeAdd} />
      </>
    );
  }

  const ariaLabel = isCompact ? intl.formatMessage(messages.tooltip, { name: active?.name ?? '' }) : undefined;
  const pillContent = (
    <ArtistSwitcherPill
      variant={variant}
      active={active}
      accountCount={switcher.ownAccountCount ?? 0}
      artistsLimit={artistsLimit}
      workspaceName={workspaceSwitch.rows.length > 1 ? (workspaceSwitch.active?.name ?? null) : null}
    />
  );

  if (sheet !== undefined) {
    return (
      <button
        type="button"
        className={toggleClass[variant]}
        onClick={sheet.onOpen}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={sheet.isOpen}
        aria-controls={sheet.isOpen ? sheet.id : undefined}
      >
        {pillContent}
      </button>
    );
  }

  return (
    <>
      <Dropdown isOpen={isMenuOpen} toggle={toggleMenu}>
        <DropdownToggle tag="button" caret={false} className={toggleClass[variant]} aria-label={ariaLabel}>
          {pillContent}
        </DropdownToggle>
        <ArtistSwitcherMenu
          accounts={accounts}
          activeAccountId={switcher.activeAccountId}
          artistsLimit={artistsLimit}
          onSelect={switcher.onSelect}
          onAddArtist={switcher.openAdd}
          onUpgradeHint={switcher.onUpgradeHint}
          className={hasWorkspaces ? styles.workspacesMenu : isCompact ? styles.compactMenu : undefined}
          end={isCompact}
          workspaceSection={
            hasWorkspaces ? (
              <WorkspaceSwitcherSection variant="menu" switcher={workspaceSwitch} onClose={closeMenu} />
            ) : undefined
          }
          isDisabled={workspaceSwitch.pendingLicenseId !== null}
        />
      </Dropdown>
      <AddArtistModal isOpen={switcher.isAddOpen} onClose={switcher.closeAdd} />
    </>
  );
};

export const ArtistSwitcher = TypedMemo(ArtistSwitcherComponent);
